import { Image } from 'expo-image';
import React, { useState } from 'react';
import { Linking, Platform, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useLoginCustomerForgotPassword } from '@/api/customers/use-login-customer-forgot-password';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Button, Input, showErrorMessage, Text } from '@/ui';

const resetImageDark = require('../../../public/images/entry/reset-pw-dark.png');
const resetImageLight = require('../../../public/images/entry/reset-pw-light.png');

interface EntryForgotPasswordProps {
  email: string;
  onBack: () => void;
}

export function EntryForgotPassword({
  email,
  onBack,
}: EntryForgotPasswordProps) {
  const { styles } = useStyles(stylesheet);
  const [sentTo, setSentTo] = useState<string>();
  const sentImage =
    UnistylesRuntime.themeName === 'dark' ? resetImageDark : resetImageLight;

  const { mutate: sendForgotPassword, isPending } =
    useLoginCustomerForgotPassword();

  const handleSend = () => {
    sendForgotPassword(
      { email },
      {
        onSuccess: () => setSentTo(email),
        onError: (e) => showErrorMessage(e.message),
      }
    );
  };

  const handleOpenMailApp = () => {
    if (Platform.OS !== 'ios') {
      Linking.openURL('mailto:');
      return;
    }

    Linking.openURL('message://').catch(() =>
      Linking.openURL('googlegmail://').catch(() => Linking.openURL('mailto:'))
    );
  };

  if (sentTo) {
    return (
      <View style={styles.sentCard}>
        <View style={styles.sentBackRow}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={onBack}
          />
        </View>

        <View style={styles.sentContent}>
          <View style={styles.sentTitleContainer}>
            <Text
              tx="entry.forgotPassword.sentTitle"
              type="title1"
              style={styles.centerText}
            />
            <Text
              tx="entry.forgotPassword.sentBody"
              values={{ email: sentTo }}
              type="bodyRegular"
              style={[styles.cardSubtitle, styles.centerText]}
            />
          </View>

          <Image
            source={sentImage}
            style={styles.sentImage}
            contentFit="contain"
          />

          <Text
            tx="entry.forgotPassword.sentCaption"
            type="caption1"
            style={[styles.cardSubtitle, styles.centerText]}
          />
        </View>

        {!isDesktop() && (
          <Button
            tx="entry.forgotPassword.openMailApp"
            type="primary"
            onPress={handleOpenMailApp}
          />
        )}
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <NavigationButton
        type={NavigationButtonTypes.back_with_text}
        onPress={onBack}
      />

      <View style={styles.titleContainer}>
        <Text tx="entry.forgotPassword.title" type="title1" />
        <Text
          tx="entry.forgotPassword.body"
          type="bodyRegular"
          style={styles.cardSubtitle}
        />
      </View>

      <View style={styles.inputsContainer}>
        <Input label="Email" value={email} disabled />
      </View>

      <View style={styles.buttonContainer}>
        <Button
          tx="entry.forgotPassword.send"
          type="primary"
          onPress={handleSend}
          disabled={isPending}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  card: {
    width: '100%',
    maxWidth: 440,
    paddingHorizontal: 18,
    gap: 16,
  },
  titleContainer: {
    gap: 8,
    marginTop: {
      xs: 24,
      md: 8,
    },
  },
  cardSubtitle: {
    color: theme.colors.text.secondary,
  },
  inputsContainer: {
    gap: 16,
  },
  buttonContainer: {
    gap: 16,
    alignItems: 'center',
  },
  sentCard: {
    flex: 1,
    width: '100%',
    maxWidth: 440,
    paddingHorizontal: 18,
    alignItems: 'center',
    // Mobile/native keeps the back button up top (content grows/centers
    // below), while desktop centers the whole group so the back button hugs
    // the content instead of floating way up the panel.
    justifyContent: {
      xs: 'flex-start',
      md: 'center',
    },
    gap: 16,
  },
  sentBackRow: {
    width: '100%',
    alignItems: 'flex-start',
  },
  sentContent: {
    width: '100%',
    // Fill the leftover space on mobile so the content stays vertically
    // centered under the top-aligned back button; on desktop it hugs the back
    // button as a centered group.
    flexGrow: {
      xs: 1,
      md: 0,
    },
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  sentTitleContainer: {
    alignItems: 'center',
    gap: 8,
  },
  centerText: {
    textAlign: 'center',
  },
  sentImage: {
    width: '100%',
    maxWidth: 320,
    aspectRatio: 406 / 346,
  },
}));
